"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";
import { DEFAULT_SUF_QUIZ_VERSION } from "@./types/suf-quiz-answers";
import {
  IGetPriceEstimateRequest,
  CheckoutAPI,
  IGetPriceEstimateResponse,
  NibblesDiscount,
  IExtraDetail,
} from "@api-clients/checkout";
import { TNibblesProduct, TSprinklesProduct } from "@api-clients/subscriptions";

import { throwIfFalsy } from "@/autovalidate";
import { PricingPlan, SubscriptionItemType } from "@/primary-models";

import {
  ISignupFlowStore,
  ICatFormData,
  CatFieldName,
  IViewState,
  IAgeViewState,
  ICatEntry,
  IUserFormData,
  IDeliveryData,
  ISubscriptionData,
} from "../types";
import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  defaultViewState,
  defaultAgeViewState,
  SIGNUP_FLOW_TTL_DAYS,
} from "../constants";
import {
  getNestedFieldValue,
  updateNestedField,
  removeNestedField,
  transformCatDataForApi,
  calculateCatBirthday,
  normalisePlanType,
} from "../utils";
import {
  transformCats,
  createCapiCustomDataFromFreshOptions,
} from "../transformer";

import { useSignupFlowErrorStore } from "./useSignUpFlowErrorStore";

const CHECKOUT_API_BASE_URL = throwIfFalsy(
  // eslint-disable-next-line @typescript-eslint/dot-notation
  process.env["NEXT_PUBLIC_CHECKOUT_URL"],
  "NEXT_PUBLIC_CHECKOUT_URL",
);
const checkoutApi = new CheckoutAPI(CHECKOUT_API_BASE_URL);

/**
 * Zustand store for managing sign-up flow data: cats, user, delivery, and subscription.
*/
export const useSignupFlowStore = create<ISignupFlowStore>()(
  persist(
    (set, get) => ({
      /* --- Initialisation ---*/
      isStoreInitialised: false,
      setIsStoreInitialised: (value: boolean) => set({ isStoreInitialised: value }),

      /* --- Version Mismatch Flag ---*/
      versionMismatchDetected: false,
      setVersionMismatchDetected: (value: boolean) => set({ versionMismatchDetected: value }),

      /* --- Cats ---*/
      cats: {},
      activeCatId: "",

      activeCat: () => get().cats[get().activeCatId],

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat },
          },
          viewState: {
            ...state.viewState,
            age: {
              ...state.viewState.age,
              [id]: defaultAgeViewState,
            },
          },
        };
      }),

      createCats: (entries: ICatEntry[]) => set((state) => {
        const updatedCats: Record<string, ICatFormData> = { ...state.cats };
        const updatedAgeViewState = { ...state.viewState.age };
        const incomingIds = new Set(entries.map((e) => e.id));

        // Remove cats and their view states that are no longer in the incoming entries
        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
            delete updatedAgeViewState[existingId];
          }
        }

        // Add/update cats and initialize their age view states
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...updatedCats[id],
            ...cat,
          };
          // Initialize age view state for new cats
          if (!updatedAgeViewState[id]) {
            updatedAgeViewState[id] = defaultAgeViewState;
          }
        }

        const firstCatId = entries[0]?.id ?? "";

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
          viewState: {
            ...state.viewState,
            age: updatedAgeViewState,
          },
        };
      }),

      updateCat: (id: string, cat: Partial<ICatFormData>) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], ...cat },
        },
        savedAt: dayjs().format(),
      })),
      getCat: (id: string) => get().cats[id],
      getActiveCatIds: () => Object.keys(get().cats).filter((catId) => !get().cats[catId].softDelete),

      softDeleteCat: (id: string) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: true },
        },
      })),

      restoreCat: (id: string) => set((state) => ({
        cats: {
          ...state.cats,
          [id]: { ...state.cats[id], softDelete: false },
        },
      })),

      migrateCatData: (fieldNames: CatFieldName[], sourceId: string, targetIds: string[]) => set((state) => {
        const source = state.cats[sourceId];
        if (!source) { return {}; }

        const updatedCats = { ...state.cats };

        const updateTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            const sourceValue = getNestedFieldValue(source, fieldName);
            if (sourceValue !== undefined) {
              target = updateNestedField(target, fieldName, sourceValue);
            }
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          updateTargetCat(targetId);
        }

        return {
          cats: updatedCats,
        };
      }),

      revertMigrateCatData: (fieldNames: CatFieldName[], targetIds: string[]) => set((state) => {
        const updatedCats = { ...state.cats };

        const removeFromTargetCat = (targetId: string) => {
          if (!updatedCats[targetId]) { return; }

          let target = { ...updatedCats[targetId] };

          for (const fieldName of fieldNames) {
            target = removeNestedField(target, fieldName);
          }

          updatedCats[targetId] = target;
        };

        for (const targetId of targetIds) {
          removeFromTargetCat(targetId);
        }

        return { cats: updatedCats };
      }),

      setActiveCatId: (id: string) => set({ activeCatId: id }),

      /* --- User ---*/
      user: defaultUser,
      updateUser: (data: Partial<IUserFormData>) => set((state) => ({
        user: {
          ...state.user,
          ...data,
        },
        savedAt: dayjs().format(),
      })),

      /* --- Delivery ---*/
      delivery: defaultDelivery,
      updateDelivery: (data: Partial<IDeliveryData>) => set((state) => ({
        delivery: {
          ...state.delivery,
          ...data,
        },
        savedAt: dayjs().format(),
      })),

      /* --- Subscription ---*/
      subscription: defaultSubscription,
      updateSubscription: (data: Partial<ISubscriptionData>) => set((state) => ({
        subscription: {
          ...state.subscription,
          ...data,
        },
        savedAt: dayjs().format(),
      })),

      /* --- View State ---*/
      viewState: defaultViewState,
      updateViewState: (
        view: string,
        params: Partial<IViewState[keyof IViewState]> | Partial<IAgeViewState>,
        catId?: string,
      ) => set((state) => {
        const viewState = { ...state.viewState };

        if (view === "age" && catId) {
          // Update age viewState for specific cat
          viewState.age = {
            ...viewState.age,
            [catId]: {
              ...viewState.age[catId],
              ...params,
            } as IAgeViewState,
          };
        } else if (view === "catParentDetails") {
          viewState.catParentDetails = { ...viewState.catParentDetails, ...params };
        } else if (view === "checkout") {
          // Update global checkout viewState
          viewState.checkout = { ...viewState.checkout, ...params };

          // Keep special logic for checkout's isDeliveryComplete
          if ("isDeliveryComplete" in params && params.isDeliveryComplete === true) {
            viewState.checkout.hasCompletedDeliveryOnce = true;
          }
        }

        return { viewState };
      }),

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        subscription: defaultSubscription,
        viewState: defaultViewState,
        sufQuizVersion: DEFAULT_SUF_QUIZ_VERSION,
        versionMismatchDetected: false, // Clear flag on reset
        savedAt: dayjs().format(), // Restart TTL timer
      }),

      /* --- Call getPriceEstimate and update store ---*/
      callGetPriceEstimateAndUpdateStore: async (
        requestedProps: Partial<IGetPriceEstimateRequest>,
      ): Promise<IGetPriceEstimateResponse> => {
        const { subscription, cats, delivery } = get();

        const baseRequestObj: IGetPriceEstimateRequest = {
          deliveryDate: dayjs(delivery.deliveryDate).format("YYYY-MM-DD"),
          discountCode: subscription.discount?.discountCode,
          discountNibbles: subscription.nibblesUpsell?.added ? NibblesDiscount.FIFTY_OFF : undefined,
          cats: transformCatDataForApi(cats, subscription),
        };

        const mergedRequestObj = { ...baseRequestObj, ...requestedProps };

        const response = await checkoutApi.getPriceEstimate(mergedRequestObj);
        if (response) {
          const { deliveryDate, discount, cats: catsPriceEstimate, totals } = response;

          // we currently only support one box of nibbles per checkout
          // cross-sell/add a new cat supports multiple boxes of nibbles so we'll need to update this logic
          // to account for quantity and type of nibbles
          const nibblesUpsell: IExtraDetail | undefined = catsPriceEstimate.flatMap((cat) => cat.trial.extras)
            .find((extra) => extra.productTitle === SubscriptionItemType.NIBBLES_CHICKEN
          || extra.productTitle === SubscriptionItemType.NIBBLES_SALMON);

          const sprinklesExtras = catsPriceEstimate
            .flatMap((cat) => cat.trial.extras)
            .filter((extra) => extra.productTitle === SubscriptionItemType.SPRINKLES);

          const totalSprinklesQuantity = sprinklesExtras
            .reduce((total, extra) => total + extra.quantity, 0);

          const sprinklesUpsell: IExtraDetail | undefined = sprinklesExtras[0]
            ? { ...sprinklesExtras[0], quantity: totalSprinklesQuantity }
            : undefined;

          // Create capiCustomData for tracking
          const capiCustomData = createCapiCustomDataFromFreshOptions(
            response,
            subscription.productType || "FRESH_TRIAL",
          );

          set({
            delivery: {
              ...get().delivery,
              deliveryDate,
            },
            subscription: {
              ...subscription,
              trial: {
                planType: catsPriceEstimate[0].trial.planType,
                totalOriginalPrice: Number.parseFloat(totals.trial.original.overall.total),
                totalDiscountedPrice: Number.parseFloat(totals.trial.discounted.overall.total),
                originalPricePerDay: Number.parseFloat(totals.trial.original.mealPlan.perCatPerDay),
                discountedPricePerDay: Number.parseFloat(totals.trial.discounted.mealPlan.perCatPerDay),
                originalPricePerPortion: Number.parseFloat(totals.trial.original.mealPlan.perPortion),
                discountedPricePerPortion: Number.parseFloat(totals.trial.discounted.mealPlan.perPortion),
              },
              ongoing: {
                planType: catsPriceEstimate[0].ongoing.planType,
                totalOriginalPrice: Number.parseFloat(totals.ongoing.original.overall.total),
                totalDiscountedPrice: Number.parseFloat(totals.ongoing.discounted.overall.total),
                originalPricePerDay: Number.parseFloat(totals.ongoing.original.mealPlan.perCatPerDay),
                discountedPricePerDay: Number.parseFloat(totals.ongoing.discounted.mealPlan.perCatPerDay),
                originalPricePerPortion: Number.parseFloat(totals.ongoing.original.mealPlan.perPortion),
                discountedPricePerPortion: Number.parseFloat(totals.ongoing.discounted.mealPlan.perPortion),
              },
              discount: {
                discountCode: discount?.code ?? "",
                type: discount?.discountType ?? "PERCENTAGE",
                value: discount?.value ?? undefined,
              },
              nibblesUpsell: nibblesUpsell
                ? {
                  added: nibblesUpsell.quantity > 0,
                  discountedPrice: Number.parseFloat(nibblesUpsell.discountedPrice),
                  originalPrice: Number.parseFloat(nibblesUpsell.totalPrice),
                  type: nibblesUpsell.productTitle as TNibblesProduct,
                  quantity: nibblesUpsell.quantity,
                }
                : subscription.nibblesUpsell,
              sprinklesUpsell: sprinklesUpsell ? {
                added: sprinklesUpsell.quantity > 0,
                discountedPrice: Number.parseFloat(sprinklesUpsell.discountedPrice),
                originalPrice: Number.parseFloat(sprinklesUpsell.unitPrice),
                type: sprinklesUpsell.productTitle as TSprinklesProduct,
                quantity: sprinklesUpsell.quantity,
              } : defaultSubscription.sprinklesUpsell,
              capiCustomData,
            },
            cats: transformCats(cats, catsPriceEstimate),
          });
        }
        return response;
      },

      /* --- Birthday calculation ---*/
      calculateAllCatsBirthdays: () => set((state) => {
        const updatedCats = { ...state.cats };

        Object.entries(updatedCats).forEach(([ catId, cat ]) => {
          if (!cat.softDelete) {
            const calculatedBirthday = calculateCatBirthday(cat);
            if (calculatedBirthday) {
              updatedCats[catId] = {
                ...cat,
                birthday: calculatedBirthday,
              };
            }
          }
        });

        return { cats: updatedCats };
      }),

      /* --- Loading state ---*/
      isLoading: false,
      setIsLoading: (state) => set({ isLoading: state }),

      /* --- Version ---*/
      sufQuizVersion: DEFAULT_SUF_QUIZ_VERSION,

      /* --- TTL ---*/
      savedAt: dayjs().format(),
    }),
    {
      name: "suf-store",
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }

        // TTL check: clear store if savedAt exists and is older than SIGNUP_FLOW_TTL_DAYS
        if (
          state.savedAt &&
          dayjs(state.savedAt).isBefore(dayjs().subtract(SIGNUP_FLOW_TTL_DAYS, "day"))
        ) {
          state.resetSignupFlow();
          useSignupFlowErrorStore.getState().clearAll();
          queueMicrotask(() => state.setIsStoreInitialised(true));
          return;
        }

        // Backfill catParentDetails for users with stale persisted stores
        if (!state.viewState.catParentDetails) {
          state.updateViewState("catParentDetails", { isTransitionVisible: false });
        }

        // Normalise legacy plan types (e.g. Kotlin "PlanTrial14x14" → PricingPlan.TRIAL)
        const trialPlan = normalisePlanType(state.subscription?.trial?.planType, PricingPlan.TRIAL);
        const ongoingPlan = normalisePlanType(state.subscription?.ongoing?.planType, PricingPlan.STANDARD);
        const planTypeChanged =
          trialPlan !== state.subscription?.trial?.planType ||
          ongoingPlan !== state.subscription?.ongoing?.planType;
        if (planTypeChanged) {
          state.updateSubscription({
            trial: { ...state.subscription?.trial, planType: trialPlan },
            ongoing: { ...state.subscription?.ongoing, planType: ongoingPlan },
          });
        }

        // Check for version mismatch and set flag (don't reset here)
        // Reset logic is handled explicitly in:
        // 1. Abandoned cart page (checks flag before restore, resets on error)
        // 2. Layout useEffect (checks flag and resets when needed)
        if (state.sufQuizVersion !== DEFAULT_SUF_QUIZ_VERSION) {
          state.setVersionMismatchDetected(true);
        }

        // Defer savedAt reset and setIsStoreInitialised so sync persist doesn't overwrite them.
        queueMicrotask(() => {
          useSignupFlowStore.setState({ savedAt: dayjs().format() });
          state.setIsStoreInitialised(true);
        });
      },
    },
  ),
);
